<script lang="ts">
  import { page } from '$app/stores';
  import EnvelopeIcon from '$icons/EnvelopeIcon.svelte';
  import LogoutIcon from '$icons/LogoutIcon.svelte';
  import MagnifyingGlass from '$icons/MagnifyingGlass.svelte';
  import UserIcon from '$icons/UserIcon.svelte';
  import CogIcon from '$icons/settings/CogIcon.svelte';
  import InboxIcon from '$icons/messages/InboxIcon.svelte';
  import { logout } from '$utils/auth/supertokens';
  import { onboardingAllowedRoutes } from '$lib/routing-utils';
  import { getContext } from 'svelte';
  import type { Writable } from 'svelte/store';
  import ColosseumMark from '$icons/logos/ColosseumMark.svelte';
  import BugIcon from '$icons/BugIcon.svelte';
  import {
    AccountRoles,
    HackathonParticipationStatus,
    HackathonPhase,
    isEternalChallengeActive,
    isHackathonActive,
  } from '$shared/lib';
  import FlagIcon from '$icons/FlagIcon.svelte';
  import Discuss from './Discuss.svelte';
  import { isAcceleratorUserFromSession, isPartnerUserFromSession } from '$shared/util';
  import UserGroupIconOutline from '$icons/users/UserGroupIconOutline.svelte';
  import BuildingIcon from '$icons/BuildingIcon.svelte';
  import ChartIcon from '$icons/ChartIcon.svelte';
  import NavItem, { type Route } from './NavItem.svelte';
  import FolderIcon from '$icons/FolderIcon.svelte';
  import type { LayoutData } from '.svelte-kit/types/src/routes/(app)/$types';
  import ExpandableNavItem from './ExpandableNavItem.svelte';
  import LightningIcon from '$icons/LightningIcon.svelte';
  import BuildIcon from '$icons/BuildIcon.svelte';
  import SignalIcon from '$icons/SignalIcon.svelte';
  import { derived } from 'svelte/store';
  import { getHackathonLink } from '$lib/constants/hackathon';
  import TrophyIcon from '$icons/TrophyIcon.svelte';
  import { FEATURE_FLAGS } from '$shared/lib';
  import InfoSquareIcon from '$icons/InfoSquareIcon.svelte';
  import DollarIcon from '$icons/DollarIcon.svelte';
  import BookIcon from '$icons/BookIcon.svelte';
  import IncompleteProfileBadge from './badges/IncompleteProfileBadge.svelte';
  import RocketIcon from '$icons/RocketIcon.svelte';
  import StopwatchIcon from '$icons/StopwatchIcon.svelte';
  import HourglassIcon from '$icons/HourglassIcon.svelte';
  import { userEternalChallengeQuery } from '$lib/api/query/eternal';

  export let data: LayoutData;
  export let showBugReportModal = false;
  export let showAnnouncementCreateModal = false;

  let routes: Record<'top' | 'hackathon' | 'eternal' | 'accelerator' | 'bottom', Route[]>;
  const unreadConversationCount = getContext<Writable<number>>('unreadConversationCount');

  const eternalChallengeQuery = userEternalChallengeQuery({
    enabled: !!data.user,
  });

  $: userRoles = {
    isInAccelerator: data.user ? isAcceleratorUserFromSession(data.user) : false,
    isAdmin: data.user?.accountRoles?.includes(AccountRoles.ADMIN),
    isPartner: data.user ? isPartnerUserFromSession(data.user) : false,
    isModerator: data.user?.accountRoles?.includes(AccountRoles.MODERATOR),
  };

  $: routes = {
    top: [
      {
        icon: BuildIcon,
        name: 'Workshops',
        path: '/workshops',
      },
      {
        icon: MagnifyingGlass,
        name: 'Find Cofounders',
        path: '/profiles',
      },
      {
        icon: InboxIcon,
        name: 'Messages',
        path: '/messages',
        badgeCount: $unreadConversationCount,
      },
      {
        icon: UserIcon,
        name: 'My Profile',
        path: `/profiles/${data.user?.username}`,
        customBadge: data.user?.profileComplete ? null : IncompleteProfileBadge,
      },
    ],
    hackathon: [
      {
        icon: SignalIcon,
        name: 'Dashboard',
        path: '/hackathon',
      },
      {
        icon: FolderIcon,
        name: 'My Project',
        path: '/hackathon/project',
        hidden: data.hackathon
          ? data.hackathon.participationStatus === HackathonParticipationStatus.NOT_REGISTERED ||
            (data.hackathon.participationStatus < HackathonParticipationStatus.PROJECT_CREATED &&
              data.hackathon.phase >= HackathonPhase.HACKATHON_ENDED)
          : false,
      },
      {
        icon: MagnifyingGlass,
        name: 'Explore Projects',
        path: getHackathonLink('exploreProjects'),
      },
      {
        icon: TrophyIcon,
        name: 'Hall of Fame',
        path: '/hackathon/hall-of-fame',
        class: 'text-amber-400',
        hidden: !data.featureFlags?.[FEATURE_FLAGS.HALL_OF_FAME],
      },
    ],
    eternal: [
      {
        icon: StopwatchIcon,
        name: 'Dashboard',
        path: '/eternal',
      },
    ],
    accelerator: [
      {
        icon: BookIcon,
        name: 'Resources',
        path: '/accelerator/resources',
        isAcceleratorRoute: true,
        hidden: !data.accelerator?.onboarded,
      },
      {
        icon: RocketIcon,
        name: 'Demo Day',
        path: '/accelerator/demo-day',
        isAcceleratorRoute: true,
        hidden: !data.accelerator?.onboarded,
      },
      {
        icon: BuildingIcon,
        name: 'Companies',
        path: '/accelerator/companies',
        isAcceleratorRoute: true,
        hidden: !data.accelerator?.onboarded,
      },
      {
        icon: UserGroupIconOutline,
        name: 'Founders & Mentors',
        path: '/accelerator/founders',
        isAcceleratorRoute: true,
        hidden: !data.accelerator?.onboarded,
      },
      {
        icon: DollarIcon,
        name: 'Investors',
        path: '/accelerator/investors',
        isAcceleratorRoute: true,
        hidden: !data.accelerator?.onboarded,
      },
      {
        icon: InfoSquareIcon,
        name: 'Get started',
        path: '/accelerator/onboarding',
        isAcceleratorRoute: true,
        hidden: data.accelerator?.onboarded,
      },
      {
        icon: EnvelopeIcon,
        name: '[ADMIN] Invite Mentors',
        path: '/accelerator/mentor-referrals',
        isAcceleratorRoute: true,
        hidden: !userRoles.isAdmin,
      },
    ],
    bottom: [
      {
        icon: null,
        name: '📣 [ADMIN] Announce',
        action: () => {
          showAnnouncementCreateModal = true;
        },
        hidden: !userRoles.isAdmin,
      },
      {
        icon: CogIcon,
        name: 'Settings',
        path: '/settings',
      },
      {
        icon: UserGroupIconOutline,
        name: 'Referrals',
        path: '/referrals',
        hidden: !userRoles.isPartner,
      },
      {
        icon: EnvelopeIcon,
        name: 'Support',
        path: '/support',
      },
      {
        icon: ChartIcon,
        name: 'Analytics',
        path: '/analytics',
      },
      {
        icon: ColosseumMark,
        name: 'Website',
        path: 'https://www.colosseum.org',
        external: true,
      },
      {
        icon: LogoutIcon,
        name: 'Logout',
        action: logout,
      },
      {
        icon: FlagIcon,
        name: '[MOD] Reports',
        path: '/reports',
        hidden: !userRoles.isModerator,
      },
      {
        icon: BugIcon,
        name: 'Bug Report',
        action: () => {
          showBugReportModal = true;
        },
        class: 'mt-4',
      },
    ],
  };

  // Expand the hackathon and accelerator routes if the path starts with the route path
  $: isHackathonRouteActive = derived(page, ($page) =>
    routes.hackathon.some((route) => route.path && $page.url.pathname.startsWith(route.path)),
  );
  $: isAcceleratorRouteActive = derived(page, ($page) =>
    routes.accelerator.some((route) => route.path && $page.url.pathname.startsWith(route.path)),
  );
  $: isEternalRouteActive = derived(page, ($page) =>
    routes.eternal.some((route) => route.path && $page.url.pathname.startsWith(route.path)),
  );
</script>

<aside class="px-sm py-2xl md:pl-2xl md:pr-lg h-full w-full">
  <nav class="flex !min-h-full flex-col justify-between">
    <div>
      <div>
        {#if data.forums}
          <Discuss forums={data.forums} />
        {/if}
        <ExpandableNavItem
          title="Eternal"
          icon={HourglassIcon}
          initiallyExpanded={$isEternalRouteActive}
          active={$eternalChallengeQuery.isSuccess &&
            !!data.user &&
            isEternalChallengeActive($eternalChallengeQuery.data.eternalChallenge)}
        >
          {#each routes.eternal as route}
            {#if !route.hidden}
              <NavItem {route} />
            {/if}
          {/each}
        </ExpandableNavItem>
        <ExpandableNavItem
          title="Hackathon"
          icon={RocketIcon}
          initiallyExpanded={$isHackathonRouteActive}
          active={data.hackathon && isHackathonActive(data.hackathon)}
        >
          {#each routes.hackathon as route}
            {#if !route.hidden}
              <NavItem {route} />
            {/if}
          {/each}
        </ExpandableNavItem>
        {#if userRoles.isInAccelerator}
          <ExpandableNavItem
            title="Accelerator"
            icon={LightningIcon}
            initiallyExpanded={$isAcceleratorRouteActive}
          >
            {#each routes.accelerator as route}
              {#if !route.hidden}
                <NavItem {route} />
              {/if}
            {/each}
          </ExpandableNavItem>
        {/if}
        {#each routes.top as route}
          {#if !route.hidden}
            <NavItem {route} />
          {/if}
        {/each}
      </div>
    </div>
    <div
      class:z-10={onboardingAllowedRoutes.has($page.url.pathname)}
      class="flex flex-col gap-y-12"
    >
      <div>
        {#each routes.bottom as route}
          {#if !route.hidden}
            <NavItem {route} />
          {/if}
        {/each}
      </div>
      <div class="px-4">
        <p class="text-xs">
          {new Date().getFullYear()} Colosseum © All Rights Reserved
        </p>
      </div>
    </div>
  </nav>
</aside>
